import React, { useState } from 'react'
import { Button, Form, Radio } from 'antd'
import { RadioChangeEvent } from 'antd/lib/radio'
import { Input } from 'components'
import Modal from 'components/Modal/Modal'
import { ExclamationCircleFilled } from '@ant-design/icons'
import { useForm } from 'antd/lib/form/Form'
import RefinancingRepository from 'egi/repositories/RefinancingRepository'
import format from 'utils/format'
import swal from 'utils/swal'
import { IRefinancingClient } from '../Refinancing/refinancingInterfaces'
import ProposalEgiRepository from 'egi/repositories/ProposalEgiRepository'

function PageContainer ({ children }: { children: React.ReactNode }) {
  return (
    <div className='position-relative flex flex-justify-center flex-align-center'>
      <div className='product-chooser-container'>{children}</div>
    </div>
  )
}

export interface IExistingRefinancingInfo {
  contractNumber: string
  proposalNumber: string
}

interface IProductChooserProps {
  callbackNewContract: (cpfCnpj: string) => void
  callbackRefinancing: (client: IRefinancingClient) => void
}

function ProductChooser ({ callbackNewContract, callbackRefinancing }: IProductChooserProps) {
  const [formRef] = useForm()
  const [subType, setSubType] = useState<'new-contract' | 'refinancing'>('new-contract')
  const [existingRefinancingInfo, setExistingRefinancingInfo] = useState<IExistingRefinancingInfo>()
  const [visibleModal, setVisibleModal] = useState(false)
  const [loading, setLoading] = useState(false)

  async function onFinish () {
    setLoading(true)

    try {
      const cpfCnpj = format.onlyCpfCnpj(formRef.getFieldValue('cpfCnpj'))

      if (subType === 'new-contract') {
        const proposalExists = await ProposalEgiRepository.checkNewContract(cpfCnpj)
        const hasProposal = proposalExists.data.data?.proposal?._id
        const hasRefin = proposalExists.data.data?.existsRefin?._id

        if (hasProposal) {
          if (hasRefin) {
            swal.basic({ title: 'Atenção!', text: 'Já existe um contrato ativo para o CPF/CNPJ informado. Se desejar um REFIN clique na opção Refinanciamento.', icon: 'warning' })
            return
          }

          swal.basic({ title: 'Atenção!', text: 'Já existe uma proposta ativa para o CPF/CNPJ informado.', icon: 'warning' })
          return
        }

        callbackNewContract(cpfCnpj)
      } else {
        const clientResponse = await RefinancingRepository.getClientData(cpfCnpj)
        const client = clientResponse.data.data?.client

        const activeRefinancingsResponse = await RefinancingRepository.getActiveRefinancings(cpfCnpj)
        const existingRefinancing = activeRefinancingsResponse.data.data

        if (existingRefinancing?.proposalNumber || existingRefinancing?.contractNumber) {
          setExistingRefinancingInfo(existingRefinancing)
          setVisibleModal(true)
          return
        }

        if (client) callbackRefinancing(client)
      }
    } catch (err: any) {
      swal.basic({ title: 'Atenção!', text: err.message, icon: 'warning' })
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  function closeModal () {
    setVisibleModal(false)
  }

  return (
    <PageContainer>
      <Modal
        className='product-chooser-modal'
        title={
          <span className='modal-title'>
            <ExclamationCircleFilled style={{ fontSize: 20 }} />
            Cliente já possui um refinanciamento em andamento
          </span>
        }
        visible={visibleModal}
        cancelText={<></>}
        onCancel={closeModal}
        onOk={closeModal}
      >
        <div className="modal-content">
          <p>O cliente informado já possui um refinanciamento em andamento no seu nome. Não é possível prosseguir com esse cliente!</p>

          <div className='modal-info'>
            {existingRefinancingInfo?.contractNumber && <p>Número do contrato: <b>{existingRefinancingInfo?.contractNumber}</b></p>}
            {existingRefinancingInfo?.proposalNumber && <p>Número da proposta: <b>{existingRefinancingInfo?.proposalNumber}</b></p>}
          </div>
        </div>
      </Modal>

      <Form form={formRef} layout='vertical' className='product-chooser-form' onFinish={onFinish}>
        <Form.Item
          name='cpfCnpj'
          label={<label className='simulator-label'>CPF/CNPJ</label>}
        >
          <Input
            placeholder='Digite o seu CPF ou CNPJ'
            className='simulator-input ant-input'
            mask='cpfcnpj'
            required
          />
        </Form.Item>

        <Form.Item
          name='sub'
          label={
            <label className='simulator-label text-left'>Tipo de produto</label>
          }
        >
          <Radio.Group
            className='w-100 simulator-radio'
            size='large'
            onChange={(event: RadioChangeEvent) =>
              setSubType(event.target.value)
            }
            defaultValue={subType}
          >
            <Radio.Button id='subProduct-new-contract' value='new-contract'>
              Novo Contrato
            </Radio.Button>
            <Radio.Button id='subProduct-refinancing' value='refinancing'>
              Refinanciamento
            </Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Button
          htmlType='submit'
          className='simulator-calculation-button w-100'
          loading={loading}
        >
          INICIAR
        </Button>
      </Form>
    </PageContainer>
  )
}

export default ProductChooser
